import { Box, Flex, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { BasicCategory } from 'src/api/v1-api';
import { useAttributeSelector } from 'src/contexts/AttributeSelector';
import useRequest from 'src/utils/hooks/useRequest';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useSize } from '@chakra-ui/react-use-size';
import * as Sentry from '@sentry/react';

interface Props {
  categories: BasicCategory[];
}

const SCROLL_DISTANCE = 320;

const FilterableCategoryCard = ({
  category,
  isSelected,
  onClick,
  height,
  width,
}: {
  category: BasicCategory;
  isSelected: boolean;
  onClick: () => void;
  height?: string;
  width?: string;
}) => {
  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      position="relative"
      overflow="hidden"
      borderRadius="lg"
      height={height}
      width={width}
      minWidth={width}
      flexShrink={0}
      border="3px solid"
      borderColor={isSelected ? '#00DA9B' : 'transparent'}
    >
      {category.image && (
        <Box
          bgImage={`url(${category.image})`}
          bgSize="cover"
          bgPosition="center"
          height="100%"
          width="100%"
        />
      )}
      <Box
        position="absolute"
        inset="0"
        bg="linear-gradient(180deg, rgba(0, 0, 0, 0) 47.92%, #000000 100%)"
      />
      <Flex
        color="white"
        direction="column-reverse"
        position="absolute"
        inset="0"
        px={{ base: '4', md: '6' }}
        py={{ base: '4', md: '6' }}
      >
        <Box fontSize="xl" fontWeight="bold" noOfLines={2}>
          {category.name}
        </Box>
      </Flex>
    </Box>
  );
};

const FilterableCategoryCards = ({ categories }: Props): JSX.Element | null => {
  const {
    setCategoryInFilters,
    selectedAttributes,
    setSelectedAttributes,
    setStagedAttributes,
    stagedAttributes,
  } = useAttributeSelector();
  const { api } = useRequest();
  const { marketplace } = useMarketplaceContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const elementRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const dimensions = useSize(elementRef);
  const theme = useTheme();

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, offsetWidth, scrollWidth } = scrollContainerRef.current;
      const threshold = 5;
      setShowLeftArrow(scrollLeft > threshold);
      setShowRightArrow(scrollLeft + offsetWidth < scrollWidth - threshold);
    }
  }, []);

  const scroll = (distance: number) => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, offsetWidth } = scrollContainerRef.current;
      const maxScrollLeft = scrollWidth - offsetWidth;
      let newScrollLeft = scrollLeft + distance;

      if (newScrollLeft < 0) {
        newScrollLeft = 0;
      } else if (newScrollLeft > maxScrollLeft) {
        newScrollLeft = maxScrollLeft;
      }

      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  const scrollContainerCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      if (node) {
        handleScroll();
      }
    },
    [handleScroll],
  );

  useEffect(() => {
    const categoryAttr = selectedAttributes['category'];
    if (categoryAttr && Array.isArray(categoryAttr) && categoryAttr.length > 0) {
      setSelectedCategoryId(categoryAttr[0]?.toString() ?? null);
    } else {
      setSelectedCategoryId(null);
    }
  }, [selectedAttributes]);

  // Use the same filtering criteria as CategoryCards component:
  // - Must have position >= 0
  // - Must not be hidden from browse (if property exists)
  // - Must have an image
  // - Only show top-level categories (level === 1)
  const sortedCategories = categories
    .filter((cat) => {
      if (cat.position < 0) return false;
      // @ts-ignore
      if (cat.hidden_from_browse) return false;
      if (!cat.image) return false;

      return cat.level === 1;
    })
    .sort((a, b) => {
      if (a.position === b.position) {
        // @ts-ignore
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        // @ts-ignore
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return aTime - bTime;
      }
      return a.position - b.position;
    });

  if (sortedCategories.length === 0) {
    return null;
  }

  const getAllDescendantIds = (category: { children?: any[] }): string[] => {
    if (!category.children || category.children.length === 0) {
      return [];
    }

    const descendantIds: string[] = [];
    for (const child of category.children) {
      descendantIds.push(child.id.toString());
      descendantIds.push(...getAllDescendantIds(child));
    }
    return descendantIds;
  };

  const handleCategoryClick = async (category: BasicCategory) => {
    if (!marketplace?.id) return;

    try {
      const categoryIdStr = category.id.toString();

      if (selectedCategoryId === categoryIdStr) {
        const newSelectedAttributes = { ...selectedAttributes };
        delete newSelectedAttributes['category'];
        setSelectedAttributes(newSelectedAttributes);

        const newStagedAttributes = { ...stagedAttributes };
        delete newStagedAttributes['category'];
        setStagedAttributes(newStagedAttributes);

        setCategoryInFilters(undefined);
        setSelectedCategoryId(null);
        return;
      }

      const response = await api.marketplace.categories.getDetails(marketplace.id, category.id);
      setCategoryInFilters(response);

      // Include the parent category ID plus all descendant category IDs
      // This ensures that listings in child categories are also shown
      const categoryIds = [categoryIdStr, ...getAllDescendantIds(response)];

      const newSelectedAttributes = {
        ...selectedAttributes,
        category: categoryIds,
      };
      setSelectedAttributes(newSelectedAttributes);

      const newStagedAttributes = {
        ...stagedAttributes,
        category: categoryIds,
      };
      setStagedAttributes(newStagedAttributes);

      setSelectedCategoryId(categoryIdStr);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'FilterableCategoryCards',
          errorType: 'handleCategoryClick',
        },
        extra: {
          categoryId: category.id,
        },
      });
    }
  };

  const numCards = sortedCategories.length;
  const containerWidth = dimensions?.width ?? 1200;
  const gap = isMobileView ? 12 : 20; // gap in pixels: base: 3 = 12px, lg: 5 = 20px

  let cardWidth: string;
  let shouldShowArrows = true;

  if (isMobileView) {
    cardWidth = `${Math.ceil(containerWidth / 1.5)}px`;
  } else {
    const totalGapWidth = (numCards - 1) * gap;
    const availableWidth = containerWidth - totalGapWidth;
    const allCardsFitWidth = availableWidth / numCards;

    if (numCards <= 3 && allCardsFitWidth >= 250) {
      cardWidth = `${Math.floor(allCardsFitWidth)}px`;
      shouldShowArrows = false;
    } else {
      cardWidth = `${Math.ceil(containerWidth / 2.5)}px`;
    }
  }

  const height = isMobileView ? '200px' : '240px';

  return (
    <Flex position="relative" direction="column" gap={4} ref={elementRef} mb={6}>
      {shouldShowArrows && showLeftArrow && (
        <Box
          position="absolute"
          left="-1"
          top="0"
          w={8}
          h="100%"
          zIndex="1"
          backgroundImage={`linear-gradient(to right, ${theme.colors.mainBG}, transparent)`}
        />
      )}
      {shouldShowArrows && showLeftArrow && !isMobileView && (
        <ChevronLeftIcon
          position="absolute"
          left={2}
          top="calc(50% - 16px)"
          w={8}
          h={8}
          zIndex="2"
          backgroundColor="gray.700"
          color="white"
          borderRadius={4}
          cursor="pointer"
          onClick={() => scroll(-SCROLL_DISTANCE)}
        />
      )}
      <Flex
        direction="row"
        gap={{ base: 3, md: 4, lg: 5 }}
        scrollSnapType="x mandatory"
        overflowX="auto"
        overflowY="hidden"
        onScroll={handleScroll}
        ref={scrollContainerCallbackRef}
        width="100%"
      >
        {sortedCategories.map((category) => (
          <FilterableCategoryCard
            key={category.id}
            category={category}
            isSelected={selectedCategoryId === category.id.toString()}
            onClick={() => handleCategoryClick(category)}
            height={height}
            width={cardWidth}
          />
        ))}
      </Flex>
      {shouldShowArrows && showRightArrow && !isMobileView && (
        <ChevronRightIcon
          position="absolute"
          right={2}
          top="calc(50% - 16px)"
          w={8}
          h={8}
          zIndex="2"
          backgroundColor="gray.700"
          color="white"
          borderRadius={4}
          cursor="pointer"
          onClick={() => scroll(SCROLL_DISTANCE)}
        />
      )}
      {shouldShowArrows && showRightArrow && (
        <Box
          position="absolute"
          right="-1"
          top="0"
          w={8}
          h="100%"
          zIndex="1"
          backgroundImage={`linear-gradient(to left, ${theme.colors.mainBG}, transparent)`}
        />
      )}
    </Flex>
  );
};

export default FilterableCategoryCards;
